import { useEffect, useRef } from 'react';
import { UilCheck, UilSetting } from '@iconscout/react-unicons';
import clsx from 'clsx';
import { VideoQuality } from '../../lib/types';

interface VideoQualityMenuProps {
    qualities: VideoQuality[];
    selectedQuality: VideoQuality;
    onSelect: (quality: VideoQuality) => void;
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
}

export function VideoQualityMenu({
    qualities,
    selectedQuality,
    onSelect,
    isOpen,
    onOpenChange,
}: VideoQualityMenuProps) {
    const menuRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node)
            ) {
                onOpenChange(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onOpenChange]);

    if (qualities.length <= 1) return null;

    return (
        <div className="relative" ref={menuRef}>
            <button
                ref={buttonRef}
                type="button"
                title="Kvalita videa"
                className="flex h-8 w-8 items-center justify-center rounded text-white transition-colors hover:bg-white/20"
                aria-label={`Kvalita videa (${selectedQuality.label})`}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                onClick={(event) => {
                    event.stopPropagation();
                    onOpenChange(!isOpen);
                }}
            >
                <UilSetting size={18} />
                <span className="sr-only">{selectedQuality.label}</span>
            </button>

            {isOpen ? (
                <div
                    role="listbox"
                    aria-label="Kvalita videa"
                    className="absolute bottom-full right-0 mb-2 min-w-[9rem] overflow-hidden rounded-lg bg-ink-900/95 py-1 text-sm text-white shadow-lg"
                >
                    <p className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-white/70">
                        Kvalita
                    </p>
                    {qualities.map((quality) => {
                        const isSelected =
                            quality.height === selectedQuality.height;

                        return (
                            <button
                                key={quality.height}
                                type="button"
                                role="option"
                                aria-selected={isSelected}
                                className={clsx(
                                    'flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors hover:bg-white/10',
                                    isSelected && 'bg-white/10'
                                )}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onSelect(quality);
                                    onOpenChange(false);
                                }}
                            >
                                <span>{quality.label}</span>
                                {isSelected ? <UilCheck size={16} /> : null}
                            </button>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
}
